import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { DashboardCard, PaginatedList } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { formatName } from "@tellescope/utils";

export interface TicketsSectionProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const TicketsSection = ({ contact }: TicketsSectionProps) => {
  const { t } = useTranslation("tickets");
  const ticketsQuery = useTellescopeInfiniteQuery({
    model: "tickets",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  const usersQuery = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  if (ticketsQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading tickets...")}</div>
        </div>
      </div>
    );
  }

  if (!ticketsQuery.data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("No tickets found")}</div>
        </div>
      </div>
    );
  }

  return (
    <PaginatedList
      className="flex-1 overflow-y-auto space-y-4 pr-2"
      onBottomReached={() => ticketsQuery.fetchNextPage()}
    >
      {ticketsQuery.data?.map((ticket) => {
        const user = usersQuery.data?.find((user) => user.id === ticket.owner);
        const assignees = user
          ? [
              {
                name: formatName(user),
                src: user.avatar,
              },
            ]
          : [];

        return (
          <DashboardCard
            key={ticket.id}
            statusLabel={
              ticket.closedAt ? t("Completed") : ticket.updatedAt > ticket.createdAt ? t("In Progress") : t("Open")
            }
            title={ticket.title}
            assignees={assignees}
            timestamp={ticket.createdAt}
            className="w-full mb-2"
          />
        );
      })}
    </PaginatedList>
  );
};
